import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { JwtModule } from '@auth0/angular-jwt';
import { MomentModule } from 'angular2-moment';

import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { Ng2CompleterModule } from 'ng2-completer';
import { NouisliderModule } from 'ng2-nouislider';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { SWIPER_CONFIG, SwiperModule } from 'ngx-swiper-wrapper';
import { FileSelectDirective } from 'ng2-file-upload';
import { Observable } from 'rxjs/Observable';
import 'hammerjs';

import {
    AngularLibModule,
    ApiService,
    ApiServiceConfig,
    AuthService,
    ChatService,
    CityFieldComponent,
    CounterComponent,
    DistanceComponent,
    FacebookComponent,
    FacebookService,
    GeolocationService,
    IsOnlineDirective,
    ListComponent,
    LoggerService,
    SocketService
} from 'angular-lib';

import { routing } from './app.routing';
import { AuthGuardService, NoAuthGuardService } from './guards/auth-guard.service';
import { environment } from '../environments/environment';

import { AppComponent } from './components/app/app.component';
import { LoginComponent } from './components/login/login.component';
import { LandingComponent } from './components/landing/landing.component';
import { LandingTeaserComponent } from './components/landing/landing-teaser/landing-teaser.component';
import { LegalsComponent } from './components/legals/legals.component';
import { RegistrationConstants } from './components/registration/registration.constants';
import { RegistrationComponent } from './components/registration/registration.component';
import { GenderComponent } from './components/registration/gender/gender.component';
import { BornDateComponent } from './components/registration/born-date/born-date.component';
import { RegisterUserComponent } from './components/registration/register-user/register-user.component';
import { LocationComponent } from './components/registration/location/location.component';
import { PhotoComponent } from './components/registration/photo/photo.component';
import { FeedbackComponent } from './components/feedback/feedback.component';
import { UnsubNotificationsComponent } from './components/feedback/unsub-notifications/unsub-notifications.component';
import { MemberComponent } from './components/member/member.component';
import { SpeedflirtComponent } from './components/speedflirt/speedflirt.component';
import { ChatlistComponent } from './components/chat/chatlist.component';
import { AlbumComponent } from './components/album/album.component';
import { EditProfileComponent } from './components/profile/edit-profile/edit-profile.component';
import { MyProfileComponent } from './components/profile/my-profile/my-profile.component';
import { SearchComponent } from './components/profile/search/search.component';
import { IndexComponent } from './components/account/index/index.component';
import { PasswordComponent } from './components/account/password/password.component';
import { IdentityComponent } from './components/account/identity/identity.component';
import { GsmComponent } from './components/account/gsm/gsm.component';
import { NotificationsComponent } from './components/account/notifications/notifications.component';
import { EmailComponent } from './components/account/email/email.component';
import { BlacklistComponent } from './components/profile/blacklist/blacklist.component';
import { LostPasswordComponent } from './components/login/lostpassword/lostpassword.component';
import { ChatComponent } from './components/chat/chat.component';
import { ChatMessageComponent } from './components/chat/childs/message/message.component';
import { ChatActionsComponent } from './components/chat/chat-actions.component';
import { MemberMenuComponent } from './components/member/member-menu.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { UploadComponent } from './components/album/upload.component';
import { LoaderComponent } from './components/loader/loader.component';
import { LoaderService } from './services/loader.service';
import { SafeHtmlPipe } from './pipes/safe-html.pipe';
import { PublicComponent } from './components/public/public.component';
import { PublicMenuComponent } from './components/public/public-menu.component';
import { MenuComponent } from './components/menu.component';
import { PublicFooterComponent } from './components/public/public-footer.component';
import { PopupComponent } from './components/popup/popup.component';
import { PopupService } from './services/popup.service';
import { ReportPopupComponent } from './components/popup/types/report/popup-report.component';
import { PictureAuthPopupComponent } from './components/popup/types/pictureAuth/popup-picture-auth.component';
import { MenuService } from './services/menu.service';
import { BlacklistUnlockPopupComponent } from './components/popup/types/blacklistUnlock/popup-blacklist-unlock.component';
import { LanguageComponent } from './components/account/language/language.component';
import { TarteaucitronComponent } from './components/tarteaucitron/tarteaucitron.component';
import { AuthInterceptor } from './http.interceptor';

import { UserPictureComponent } from './components/user-picture/user-picture.component';

import { PhotoSortPipe } from './pipes/photo-sort';
import { ToastComponent } from './components/toast/toast.component';
import { ToastService } from './services/toast.service';
import { IovationComponent } from './components/iovation.component';
import { DeletePicturePopupComponent } from './components/popup/types/deletePicture/popup-delete-picture.component';
import { QuestionComponent } from './components/chat/childs/question/question.component';
import { SystemComponent } from './components/chat/childs/system/system.component';
import { ChatUnreadBubbleComponent } from './components/chat-unread-bubble/chat-unread-bubble.component';


export class EzTranslate implements TranslateLoader {

    constructor(private api: ApiService) {}

    /**
     * TranslateLoader implementation
     *
     * @param lang
     * @returns {Observable<any>}
     */
    public getTranslation(lang: string): Observable<any> {
        return this.api.getTranslation(lang);
    }
}

@NgModule({
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        FormsModule,
        HttpClientModule,
        JwtModule.forRoot({
            config: {
                tokenGetter: () => {
                    return AuthService.getToken();
                },
                whitelistedDomains: [environment.host]
            }
        }),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useClass: EzTranslate,
                deps: [ApiService]
            }
        }),
        routing,
        Ng2CompleterModule,
        AngularLibModule,
        NouisliderModule,
        InfiniteScrollModule,
        SwiperModule,
        MomentModule
    ],
    declarations: [
        AppComponent,
        ChatComponent,
        ChatMessageComponent,
        LoginComponent,
        MemberComponent,
        LandingComponent,
        LandingTeaserComponent,
        RegistrationComponent,
        GenderComponent,
        ListComponent,
        BornDateComponent,
        SpeedflirtComponent,
        RegisterUserComponent,
        LegalsComponent,
        LocationComponent,
        PhotoComponent,
        FileSelectDirective,
        FacebookComponent,
        CityFieldComponent,
        DistanceComponent,
        AlbumComponent,
        FeedbackComponent,
        UnsubNotificationsComponent,
        CounterComponent,
        MemberComponent,
        SpeedflirtComponent,
        ChatlistComponent,
        AlbumComponent,
        UploadComponent,
        EditProfileComponent,
        ChatActionsComponent,
        IsOnlineDirective,
        MenuComponent,
        MemberMenuComponent,
        PublicMenuComponent,
        LoaderComponent,
        MyProfileComponent,
        SearchComponent,
        IndexComponent,
        PasswordComponent,
        IdentityComponent,
        GsmComponent,
        NotificationsComponent,
        EmailComponent,
        BlacklistComponent,
        LostPasswordComponent,
        PublicComponent,
        PublicFooterComponent,
        SafeHtmlPipe,
        PhotoSortPipe,
        PopupComponent,
        ReportPopupComponent,
        PictureAuthPopupComponent,
        BlacklistUnlockPopupComponent,
        DeletePicturePopupComponent,
        LanguageComponent,
        UserPictureComponent,
        ToastComponent,
        TarteaucitronComponent,
        IovationComponent,
        QuestionComponent,
        SystemComponent,
        ChatUnreadBubbleComponent
    ],
    providers: [
        {
            provide: ApiService,
            useFactory: (http: HttpClient) => {
                return new ApiService(new ApiServiceConfig({
                    host: environment.host,
                    scheme: environment.scheme,
                    staticHost: environment.statichost,
                    siteId: environment.siteId
                }), http);
            },
            deps: [HttpClient]
        },
        {
            provide: SocketService,
            useFactory: () => new SocketService(environment.socketIo)
        },
        AuthService,
        AuthGuardService,
        NoAuthGuardService,
        FacebookService,
        RegistrationConstants,
        GeolocationService,
        LoaderService,
        PopupService,
        MenuService,
        LoggerService,
        ToastService,
        {
            provide: SWIPER_CONFIG,
            useValue: {}
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true
        },
        ChatService
    ],
    // Dynamically loaded components:
    // @see http://stackoverflow.com/questions/38093727/angular2-insert-a-dynamic-component-as-child-of-a-container-in-the-dom
    entryComponents: [
        LandingTeaserComponent,
        GenderComponent,
        BornDateComponent,
        RegisterUserComponent,
        LocationComponent,
        PhotoComponent,
        FeedbackComponent,
        ChatMessageComponent,
        UnsubNotificationsComponent,
        ChatActionsComponent,
        ReportPopupComponent,
        PictureAuthPopupComponent,
        BlacklistUnlockPopupComponent,
        DeletePicturePopupComponent,
        ToastComponent,
        QuestionComponent,
        SystemComponent
    ],
    bootstrap: [ AppComponent ]
})
export class AppModule {}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/@angularclass/hmr-loader!./src/app/app.module.ts